import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { TrendingUp, DollarSign, ShoppingBag, Percent, AlertTriangle, Clock, FileText } from "lucide-react";
import UnitScopeTabs from "../../components/UnitScopeTabs";
import DetalheVendasModal from "../../components/DetalheVendasModal";

const money = (v) => `R$ ${Number(v).toFixed(2).replace(".", ",")}`;

export default function MasterDashboard() {
  const [data, setData] = useState(null);
  const [unidades, setUnidades] = useState([]);
  const [scope, setScope] = useState("global"); // global | unidade_id
  const [modalOpen, setModalOpen] = useState(false);
  const [vendasDetalhe, setVendasDetalhe] = useState([]);

  useEffect(() => {
    api.get("/unidades").then((r) => setUnidades(r.data));
  }, []);

  useEffect(() => {
    const params = scope === "global" ? {} : { unidade_id: scope };
    api.get("/relatorios/dashboard", { params }).then((r) => setData(r.data));
  }, [scope]);

  const abrirDetalhe = async () => {
    // Carrega vendas dos últimos 30 dias com todos os detalhes
    const hoje = new Date();
    const dias30 = new Date();
    dias30.setDate(hoje.getDate() - 30);
    const params = {
      data_inicio: dias30.toISOString().slice(0, 10),
    };
    if (scope !== "global") params.unidade_id = scope;
    const r = await api.get("/vendas", { params });
    const units = unidades.reduce((acc, u) => ({ ...acc, [u.id]: u.nome }), {});
    setVendasDetalhe(
      r.data.map((v) => ({ ...v, unidade_nome: units[v.unidade_id] || null }))
    );
    setModalOpen(true);
  };

  if (!data) return <div className="text-sm text-neutral-500">Carregando...</div>;

  const COLORS = { santa_clara: "#2563EB", lacador: "#DC2626" };

  const unitCards = data.unidades.map((u) => ({
    slug: u.slug,
    nome: u.nome,
    color: COLORS[u.slug] || "#0A0A0A",
    fat: data.total_por_unidade[u.slug] || 0,
    lucro: data.lucro_por_unidade[u.slug] || 0,
    qtd: data.vendas_por_unidade[u.slug] || 0,
  }));

  return (
    <div>
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
          Consolidado · Últimos 30 dias
        </p>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
          Dashboard Geral
        </h2>
      </div>

      <UnitScopeTabs unidades={unidades} value={scope} onChange={setScope} />

      <div className="flex justify-end mb-4">
        <button
          data-testid="dashboard-detalhe-btn"
          onClick={abrirDetalhe}
          className="bg-yellow-400 hover:bg-yellow-500 text-neutral-950 h-10 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2 cta-brutal"
        >
          <FileText className="w-4 h-4" /> Relatório detalhado · CSV · Térmico
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard
          icon={DollarSign}
          label="Faturamento"
          value={money(data.faturamento_total)}
          accent="bg-neutral-950"
        />
        <MetricCard
          icon={TrendingUp}
          label="Lucro Líquido"
          value={money(data.lucro_total)}
          accent="bg-green-600"
        />
        <MetricCard
          icon={ShoppingBag}
          label="Vendas"
          value={data.qtd_vendas_total}
          accent="bg-yellow-400 text-neutral-950"
        />
        <MetricCard
          icon={Percent}
          label="Margem"
          value={`${
            data.faturamento_total > 0
              ? ((data.lucro_total / data.faturamento_total) * 100).toFixed(1)
              : 0
          }%`}
          accent="bg-blue-600"
        />
      </div>

      {/* Comparativo de unidades - mostra apenas quando global */}
      {scope === "global" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {unitCards.map((u) => (
            <div
              key={u.slug}
              className="bg-white border-2 border-neutral-900 p-5"
              data-testid={`unit-card-${u.slug}`}
            >
              <div
                className="w-12 h-2 mb-3"
                style={{ backgroundColor: u.color }}
              />
              <h3 className="font-display text-xl font-black tracking-tight">{u.nome}</h3>
              <div className="grid grid-cols-3 gap-3 mt-4">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">
                    Faturamento
                  </div>
                  <div className="font-mono font-bold text-lg">{money(u.fat)}</div>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">
                    Lucro
                  </div>
                  <div className="font-mono font-bold text-lg text-green-700">
                    {money(u.lucro)}
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">
                    Vendas
                  </div>
                  <div className="font-mono font-bold text-lg">{u.qtd}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border-2 border-neutral-900 p-5 mb-6">
        <h3 className="font-display font-bold uppercase mb-4">Evolução Diária (R$)</h3>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <LineChart data={data.serie_diaria}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis
                dataKey="data"
                fontSize={10}
                tick={{ fontFamily: "JetBrains Mono" }}
                tickFormatter={(d) => d.slice(5)}
              />
              <YAxis fontSize={10} tick={{ fontFamily: "JetBrains Mono" }} />
              <Tooltip formatter={(v) => money(v)} />
              <Legend />
              {scope === "global" ? (
                data.unidades.map((u) => (
                  <Line
                    key={u.slug}
                    type="monotone"
                    dataKey={u.slug}
                    stroke={COLORS[u.slug] || "#0A0A0A"}
                    strokeWidth={2}
                    name={u.nome}
                    dot={false}
                  />
                ))
              ) : (
                <Line
                  type="monotone"
                  dataKey={unidades.find((u) => u.id === scope)?.slug}
                  stroke={COLORS[unidades.find((u) => u.id === scope)?.slug] || "#0A0A0A"}
                  strokeWidth={2}
                  name={unidades.find((u) => u.id === scope)?.nome || ""}
                  dot={false}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {scope === "global" && (
        <div className="bg-white border-2 border-neutral-900 p-5 mb-6">
          <h3 className="font-display font-bold uppercase mb-4">Faturamento por Unidade</h3>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={unitCards}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="nome" fontSize={11} />
                <YAxis fontSize={10} />
                <Tooltip formatter={(v) => money(v)} />
                <Bar dataKey="fat" name="Faturamento">
                  {unitCards.map((u, i) => (
                    <Bar key={i} fill={u.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Últimas Vendas + Estoque Baixo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border-2 border-neutral-900 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <h3 className="font-display font-bold uppercase">Últimas Vendas</h3>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
              {data.ultimas_vendas?.length || 0} mais recentes
            </span>
          </div>
          {(!data.ultimas_vendas || data.ultimas_vendas.length === 0) ? (
            <p className="text-sm text-neutral-500 py-6 text-center">Sem vendas ainda</p>
          ) : (
            <ul className="space-y-2 max-h-[320px] overflow-auto">
              {data.ultimas_vendas.map((v) => (
                <li
                  key={v.id}
                  data-testid={`dashboard-venda-${v.id}`}
                  className="border border-neutral-200 p-2 hover:bg-neutral-50"
                >
                  <div className="flex items-center gap-2">
                    {v.unidade_slug && (
                      <span
                        className="w-1 h-10 block"
                        style={{
                          backgroundColor:
                            v.unidade_slug === "santa_clara" ? "#2563EB" : "#DC2626",
                        }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-display font-bold text-sm">
                          {v.vendedor_nome}
                        </span>
                        <span className="font-mono font-bold text-sm">
                          {money(v.total_liquido)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-0.5">
                        <span className="text-xs text-neutral-600">
                          {v.cliente_nome || "—"}
                          {v.placa && (
                            <span className="font-mono ml-2">· {v.placa}</span>
                          )}
                        </span>
                        <span className="font-mono text-[10px] text-neutral-500">
                          {new Date(v.data_venda).toLocaleString("pt-BR", {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                      <div className="font-mono text-[10px] text-neutral-500 mt-0.5">
                        {v.qtd_itens} itens · {v.unidade_nome}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border-2 border-neutral-900 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <h3 className="font-display font-bold uppercase">Estoque Baixo</h3>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
              &lt; 10 unidades
            </span>
          </div>
          {(!data.estoque_baixo || data.estoque_baixo.length === 0) ? (
            <p className="text-sm text-neutral-500 py-6 text-center">
              Nenhum item em estoque baixo
            </p>
          ) : (
            <ul className="space-y-2 max-h-[320px] overflow-auto">
              {data.estoque_baixo.map((e, i) => {
                const isZero = e.quantidade_atual === 0;
                return (
                  <li
                    key={`${e.produto_id}-${e.unidade_id}-${i}`}
                    data-testid={`dashboard-estoque-baixo-${e.produto_id}-${e.unidade_id}`}
                    className={`border-2 p-2 flex items-center gap-2 ${
                      isZero
                        ? "border-red-600 bg-red-50"
                        : "border-yellow-500 bg-yellow-50"
                    }`}
                  >
                    {e.unidade_slug && (
                      <span
                        className="w-1 h-10 block"
                        style={{
                          backgroundColor:
                            e.unidade_slug === "santa_clara" ? "#2563EB" : "#DC2626",
                        }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-sm truncate">
                        {e.produto_nome}
                      </div>
                      <div className="font-mono text-[10px] text-neutral-600 uppercase tracking-widest">
                        {e.categoria} · {e.unidade_nome}
                      </div>
                    </div>
                    <div
                      className={`font-mono font-black text-2xl px-2 ${
                        isZero ? "text-red-700" : "text-yellow-700"
                      }`}
                    >
                      {e.quantidade_atual}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <DetalheVendasModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        titulo="Dashboard · Últimos 30 dias"
        escopo={
          scope === "global"
            ? "GLOBAL"
            : unidades.find((u) => u.id === scope)?.nome || ""
        }
        periodo="Últimos 30 dias"
        vendas={vendasDetalhe}
        metricas={
          data
            ? [
                { label: "FAT LIQ", value: money(data.faturamento_total) },
                { label: "LUCRO", value: money(data.lucro_total) },
                { label: "VENDAS", value: data.qtd_vendas_total },
              ]
            : []
        }
      />
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border-2 border-neutral-900 p-4">
      <div
        className={`w-10 h-10 ${accent} flex items-center justify-center text-white mb-3`}
      >
        <Icon className="w-5 h-5" strokeWidth={2.5} />
      </div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
        {label}
      </div>
      <div className="font-display font-black text-2xl tracking-tight">{value}</div>
    </div>
  );
}
